/**
 * Realtime token request
 *
 * Fetches an Ably token request from the backend for the realtime client.
 * Extracted from the hook so the retry/refresh decision is unit-testable
 * without the Ably SDK.
 *
 * Key behavior (fixes the reconnect storm in issue #527):
 *  - On 401 we refresh the access token ONCE and retry. The previous code read
 *    the access token directly and never refreshed, so an expired token meant a
 *    permanent 401 -> Ably reconnect -> 401 loop.
 *  - A 401 that a refresh can't fix (no refresh token, or refresh failed) and a
 *    403 (not authorized for the requested channels) are marked `terminal`: they
 *    will recur on every reconnect, so the caller must STOP (close the client)
 *    rather than let Ably loop forever.
 *  - A 503 is transient: back off and retry; if exhausted, throw a NON-terminal
 *    error so the caller can let Ably reconnect later.
 */

import type { TokenRequest } from "ably"
import { refreshAccessToken } from "./auth-refresh"
import { getAccessToken, getRefreshToken } from "./token-manager"

const RETRYABLE_STATUS = 503
const MAX_TOKEN_RETRIES = 3

export interface RealtimeTokenError extends Error {
  /** HTTP status, or 0 for a network-level failure (fetch rejected). */
  status: number
  /** True when the failure will recur on reconnect -> caller should stop. */
  terminal: boolean
  /** Underlying error (refresh failure / network error), if any. The TS lib in
   *  this project doesn't expose Error.cause, so declare it explicitly. */
  cause?: unknown
}

/** Type guard so callers narrow a caught `unknown` instead of an unsafe cast. */
export const isRealtimeTokenError = (error: unknown): error is RealtimeTokenError =>
  error instanceof Error && typeof (error as Partial<RealtimeTokenError>).terminal === "boolean"

const makeTokenError = (status: number, terminal: boolean, cause?: unknown): RealtimeTokenError => {
  const error = new Error(`Failed to fetch realtime token (${status})`) as RealtimeTokenError
  error.status = status
  error.terminal = terminal
  if (cause !== undefined) {
    error.cause = cause
  }
  return error
}

const wait = async (delayMs: number): Promise<void> => {
  await new Promise(resolve => {
    setTimeout(resolve, delayMs)
  })
}

export interface RequestRealtimeTokenOptions {
  tokenUrl: string
  /** Called when a transient (503) failure triggers a backoff retry. */
  onReconnecting?: () => void
}

export const requestRealtimeToken = async ({
  tokenUrl,
  onReconnecting,
}: RequestRealtimeTokenOptions): Promise<TokenRequest> => {
  let refreshed = false

  for (let attempt = 0; attempt < MAX_TOKEN_RETRIES; attempt += 1) {
    const accessToken = getAccessToken()
    if (!accessToken) {
      // No token at all -> nothing to authorize with, and reconnecting won't help.
      throw makeTokenError(401, true)
    }

    let response: Response
    try {
      response = await fetch(tokenUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
    } catch (networkError) {
      // fetch REJECTS (vs resolving to a !ok Response) on offline / DNS / TLS /
      // abort. Treat as transient so Ably can reconnect, but classify it (rather
      // than letting a raw TypeError fall through un-typed) and keep the cause.
      throw makeTokenError(0, false, networkError)
    }

    if (response.ok) {
      const payload = (await response.json()) as { data: TokenRequest }
      return payload.data
    }

    // Expired/invalid access token: refresh once, then retry. If we've already
    // refreshed this call, or there's no refresh token, or the refresh itself
    // fails, it's terminal -> the caller must stop reconnecting.
    if (response.status === 401) {
      if (refreshed || !getRefreshToken()) {
        throw makeTokenError(401, true)
      }
      refreshed = true
      try {
        await refreshAccessToken()
      } catch (refreshError) {
        // Keep the refresh failure's cause so the Sentry capture shows WHY the
        // refresh failed (refresh-endpoint 401 vs network vs malformed body).
        throw makeTokenError(401, true, refreshError)
      }
      continue
    }

    // Not authorized -> will recur on reconnect, so stop.
    if (response.status === 403) {
      throw makeTokenError(403, true)
    }

    // Transient backend unavailability -> back off and retry.
    if (response.status === RETRYABLE_STATUS && attempt < MAX_TOKEN_RETRIES - 1) {
      onReconnecting?.()
      await wait(2 ** attempt * 1000)
      continue
    }

    // Other / exhausted: non-terminal so the caller may let Ably reconnect later.
    throw makeTokenError(response.status, false)
  }

  throw makeTokenError(RETRYABLE_STATUS, false)
}
